/**
 * Per-stage, per-extension Chrome Web Store listing URLs — the canonical
 * source of truth for the frontend.
 *
 * It lives in `shared-fe` because the table used to be duplicated in
 * `web/src/config/cwsLinks.ts` + `extension/src/shared/cwsLinks.ts`, and
 * the inter-package rule forbids `extension → web` so neither could own
 * it. PR #236 follow-up hoisted it here and deleted both duplicates,
 * eliminating the two-place-update trap.
 *
 * ACTUAL CONSUMERS TODAY: `@nu-assist/web` only — `SequencesPage.tsx`
 * (Install NUCCE), `BabyChopsV2Tool.tsx` (Install Baby Chops) and
 * `ChopbookTool.tsx` (Install Chopbook Tool). The extension package
 * references nothing in this module; it is placed here so the extension CAN
 * consume it, not because it does.
 *
 * THREE EXTENSION LINES, keyed by `ExtensionName` (from the leaf
 * `../domain/extension`):
 *
 *   - `nucce`       — the nu-assist / Thrive extension.
 *   - `baby-chops`  — the LinkedIn Recruiter scraper (its OWN CWS listings,
 *                     its own ids; split from NUCCE 2026-07-06).
 *   - `chopbook`    — the PitchBook capture tool (its own listings + ids;
 *                     added 2026-08-05, prod listing registered 2026-08-18).
 *
 * That is the same type `useLatestExtensionVersion({ extension })` sends as
 * `?extension=`, so a page resolves its version and its install link from ONE
 * key and cannot pair Baby Chops' version with NUCCE's install button. Scope
 * of that guarantee: it holds across the frontend. The API declares its own
 * hand-written `Literal[...]` of the same names
 * (`packages/api/app/web/v2/extension/router.py`) which is a mirror, not a
 * derived type — FE and BE can still drift, and adding a third extension means
 * editing both.
 *
 * The CWS listing URL grammar (from
 * `chromewebstore.google.com/detail/<slug>/<id>`):
 *   - `<id>`  = the extension's CWS-assigned extension id (32-char a..p).
 *               Source of truth: `.agent/SOP/chrome-extension/extension-ids.md`.
 *   - `<slug>`= the URL-friendly slug derived from the listing's `name`
 *               field on first publish. Dev's manifest `name` is
 *               `"nu-assist (dev)"` → slug `nu-assist-dev`. Prod's
 *               manifest `name` is `"nu-assist"` → slug `nu-assist`.
 *               Baby Chops: `"Baby Chops (Dev)"` → `baby-chops-dev`,
 *               `"Baby Chops"` → `baby-chops`. Chopbook:
 *               `"Chopbook Tool (Dev)"` → `chopbook-tool-dev`,
 *               `"Chopbook Tool"` → `chopbook-tool` (DERIVED from the name,
 *               never read back from the live URL — see the redirect note
 *               below for why that is safe).
 *               See `packages/extension/src/manifest/stage-dev.ts`,
 *               `stage-prod.ts`, `stage-baby-chops-{dev,prod}.ts`.
 *
 * The slug is COSMETIC, verified 2026-08-11 against the four listings that
 * existed then: CWS 301-redirects `/detail/<anything>/<id>` to its own
 * canonical slug for that id, so a stale slug still lands on the right
 * listing. The id is the load-bearing half. That is what makes the derived
 * (never read back) `chopbook-tool` slug recorded on 2026-08-18 acceptable:
 * a Private listing exposes no public title to an anonymous fetcher, so there
 * is no observed slug to copy, and the redirect lands users correctly anyway.
 * (The four listings verified in 2026-08-11 redirect to
 * `/detail/empty-title/<id>` — a private/trusted-testers listing exposes no
 * public title.)
 *
 * A stage with no REGISTERED listing returns `null` from
 * `getCwsListingUrl` so callers MUST handle that branch rather than getting
 * a broken URL. Today that is `local` for all three lines (all are
 * Load-Unpacked-only builds), even though `local` does have a real derived
 * extension id — the SLUG is what is absent.
 *
 * Note this module answers "where is the listing", never "may this user
 * install it" — and never "is it published". A non-null return means a slug
 * and an id are recorded, nothing more: CWS visibility (Private, trusted-tester
 * audience, Workspace scoping) and publication state live entirely outside this
 * repo, so callers needing that guarantee have nothing to read here. Rendering
 * an install button off a non-null return therefore trusts whoever filled in
 * the row; `extension-ids.md` is where that is tracked for humans.
 *
 * The prod URLs won't resolve for a user until CWS approves + shares the
 * listing with them; that is an operator/Google action, not a code change —
 * the URL is correct the moment the listing goes live.
 *
 * Pure functions — no env reads, no DOM, no `chrome.*` — safe to call
 * from any surface (web, extension popup, extension content scripts,
 * extension service worker).
 */
import type { ExtensionName } from "../domain/extension";
import { EXTENSIONS } from "./extensions.generated";
import type { Stage } from "./getStageLabel";

/**
 * Slug + id both come from the REGISTRY now
 * (`packages/python-shared/nu_assist_shared/extensions.json`, mirrored here by
 * `pnpm gen:extension-registry`). This file used to carry its own copy of both
 * tables, under a comment naming the SOP markdown as the source of truth and
 * instructing the reader to "update BOTH this file and the SOP in the same PR"
 * — which is the trap the registry exists to remove, and this module is the
 * one the live Install CTA actually renders from.
 *
 * Key grammar, and the ONE assumption this derivation makes: a registry key is
 * the bare `Stage` for `nucce` (`local` / `dev` / `prod`) and
 * `<product>-<stage>` for every other line (`baby-chops-dev`,
 * `chopbook-dev`, …). `extensions.generated.test.ts` pins the registry against
 * the canonical JSON, and `cwsLinks.test.ts` pins these lookups against the
 * ids they must resolve to, so a key-grammar change surfaces as a red test
 * rather than a silently-null Install CTA.
 *
 * A missing (product, stage) pair resolves to `null` — which is exactly right
 * for the pairs that do not exist: every `*-local` is Load-Unpacked only.
 * `chopbook-prod` resolves as of 2026-08-18 (listing created); before that it
 * was the one published-stage pair that did not, and `ChopbookTool.tsx`
 * rendered a "listing still being created" card on prod because of it.
 */
function registryKey(extension: ExtensionName, stage: Stage): string {
  return extension === "nucce" ? stage : `${extension}-${stage}`;
}

const STAGES: readonly Stage[] = ["local", "dev", "prod"];
const EXTENSION_NAMES: readonly ExtensionName[] = ["nucce", "baby-chops", "chopbook"];

function tableOf<K extends "slug" | "id">(
  field: K,
): Record<ExtensionName, Record<Stage, string | null>> {
  return Object.fromEntries(
    EXTENSION_NAMES.map((extension) => [
      extension,
      Object.fromEntries(
        STAGES.map((stage) => [
          stage,
          EXTENSIONS[registryKey(extension, stage)]?.[field] ?? null,
        ]),
      ) as Record<Stage, string | null>,
    ]),
  ) as Record<ExtensionName, Record<Stage, string | null>>;
}

/**
 * Per-extension, per-stage CWS listing slug — `null` for stages that are
 * never published to the Web Store.
 */
const CWS_SLUG_BY_STAGE = tableOf("slug");

/** Per-extension, per-stage extension id — `null` where no build exists. */
const EXTENSION_ID_BY_STAGE = tableOf("id");

/** CWS listing URL host prefix. */
const CWS_LISTING_PREFIX = "https://chromewebstore.google.com/detail";

/**
 * Compute the CWS listing URL for a stage + extension, or `null` if that
 * combination is never published (currently any `local`).
 *
 * `extension` defaults to `nucce` so existing 1-arg callers keep their
 * meaning.
 */
export function getCwsListingUrl(
  stage: Stage,
  extension: ExtensionName = "nucce",
): string | null {
  const slug = CWS_SLUG_BY_STAGE[extension][stage];
  const id = EXTENSION_ID_BY_STAGE[extension][stage];
  if (slug === null || id === null) return null;
  return `${CWS_LISTING_PREFIX}/${slug}/${id}`;
}

/**
 * Lookup helper for the per-stage CWS extension id. Returns `null` for
 * stages with no id at all. Surfaced so callers that need the raw id
 * (e.g. for analytics / debugging) don't have to re-parse it out of the
 * listing URL.
 *
 * Note this is NOT the same predicate as `getCwsListingUrl` returning
 * `null`: `local` has a real derived id but no published listing.
 */
export function getCwsExtensionId(
  stage: Stage,
  extension: ExtensionName = "nucce",
): string | null {
  return EXTENSION_ID_BY_STAGE[extension][stage];
}
